import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { openInvoice } from '@/lib/invoice';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, ArrowRight, Repeat, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const { refresh } = useCart();
  const { format } = useCurrency();
  const { t } = useLanguage();

  useEffect(() => { api.get('/orders').then(({ data }) => setOrders(data)); }, []);

  const statusMap = {
    placed: { label: t('orders.placed', 'Placed'), color: 'bg-sky-400/15 text-sky-100 border-sky-300/20' },
    in_production: { label: t('orders.inProduction', 'In Production'), color: 'bg-amber-400/15 text-amber-100 border-amber-300/20' },
    shipped: { label: t('orders.shipped', 'Shipped'), color: 'bg-indigo-400/15 text-indigo-100 border-indigo-300/20' },
    delivered: { label: t('orders.delivered', 'Delivered'), color: 'bg-emerald-400/15 text-emerald-100 border-emerald-300/20' },
    cancelled: { label: t('orders.cancelled', 'Cancelled'), color: 'bg-rose-400/15 text-rose-100 border-rose-300/20' },
  };

  const reorder = async (orderId) => {
    try {
      const { data } = await api.post(`/orders/${orderId}/reorder`);
      await refresh();
      toast.success(t('orders.reorderToast', `Added ${data.added} items to cart`).replace('{n}', data.added));
    } catch (e) { toast.error(t('orders.reorderFailed', 'Reorder failed')); }
  };

  const handleInvoice = async (orderId) => {
    const ok = await openInvoice(orderId);
    if (!ok) toast.error('Could not open invoice');
  };

  if (orders.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <Package className="w-10 h-10 text-white/40 mx-auto" />
        <h1 className="font-display text-3xl font-bold mt-4">{t('orders.empty', 'No orders yet')}</h1>
        <p className="text-white/60 mt-2">{t('orders.emptyDesc', 'Once you place an order, it will appear here.')}</p>
        <Link to="/shop"><Button data-testid="orders-shop-button" className="mt-6 bg-white text-[color:var(--jw-navy-900)] hover:bg-white/90">{t('orders.startShopping', 'Start shopping')}</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('orders.title', 'Your Orders')}</h1>
      <div className="mt-6 space-y-3">
        {orders.map(o => (
          <Card key={o.id} className="jw-card p-5" data-testid={`order-${o.id}-card`}>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <p className="font-semibold text-white">{o.order_number}</p>
                <p className="text-white/60 text-xs">{t('orders.placedOn', 'Placed')} {new Date(o.created_at).toLocaleDateString()} · {o.items?.length} {t('checkout.items', 'items')}</p>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <Badge className={`${statusMap[o.status]?.color} border`}>{statusMap[o.status]?.label || o.status}</Badge>
                <span className="text-white font-semibold">{format(o.total)}</span>
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Link to={`/orders/${o.id}`}><Button variant="secondary" size="sm" data-testid={`order-${o.id}-view-button`} className="bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]">{t('orders.track', 'Track order')} <ArrowRight className="w-4 h-4 ml-1 jw-flip-in-rtl" /></Button></Link>
              <Button variant="secondary" size="sm" onClick={() => handleInvoice(o.id)} data-testid={`order-${o.id}-invoice-button`} className="bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]"><FileDown className="w-4 h-4 mr-1" /> {t('orders.invoice', 'Invoice (SAR)')}</Button>
              <Button variant="secondary" size="sm" onClick={() => reorder(o.id)} data-testid={`order-${o.id}-reorder-button`} className="bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]"><Repeat className="w-4 h-4 mr-1" /> {t('orders.reorder', 'Reorder')}</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Orders;
